import React, { useEffect, useState } from 'react';
import { companyAdminAPI } from '../../lib/walletApi';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  FileSpreadsheet, Loader2, CheckCircle, Clock, XCircle,
  AlertTriangle, Send, DollarSign, ChevronDown, ChevronUp,
  Banknote, Users, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  draft: { icon: Clock, bg: 'bg-slate-100', text: 'text-slate-600', label: 'Draft' },
  pending_approval: { icon: Clock, bg: 'bg-amber-50', text: 'text-amber-600', label: 'Pending Approval' },
  approved: { icon: CheckCircle, bg: 'bg-blue-50', text: 'text-blue-600', label: 'Approved' },
  funded: { icon: DollarSign, bg: 'bg-emerald-50', text: 'text-emerald-600', label: 'Funded' },
  processing: { icon: Loader2, bg: 'bg-purple-50', text: 'text-purple-600', label: 'Processing' },
  completed: { icon: CheckCircle, bg: 'bg-green-50', text: 'text-green-700', label: 'Completed' },
  partially_completed: { icon: AlertTriangle, bg: 'bg-orange-50', text: 'text-orange-600', label: 'Partial' },
  failed: { icon: XCircle, bg: 'bg-red-50', text: 'text-red-600', label: 'Failed' },
  cancelled: { icon: XCircle, bg: 'bg-gray-100', text: 'text-gray-500', label: 'Cancelled' },
};

export default function PayrollManagement() {
  const [payrolls, setPayrolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [items, setItems] = useState({});
  const [loadingItems, setLoadingItems] = useState({});
  const [fundDialog, setFundDialog] = useState(null);
  const [fundRef, setFundRef] = useState('');
  const [fundAmount, setFundAmount] = useState('');
  const [disbursing, setDisbursing] = useState({});
  const [disburseResults, setDisburseResults] = useState(null);
  const [actionLoading, setActionLoading] = useState({});

  const load = async () => {
    try { setPayrolls(await companyAdminAPI.getPayrolls()); }
    catch { toast.error('Failed to load payrolls'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const loadItems = async (payrollId) => {
    if (items[payrollId]) return;
    setLoadingItems(prev => ({ ...prev, [payrollId]: true }));
    try {
      const data = await companyAdminAPI.getPayrollItems(payrollId);
      setItems(prev => ({ ...prev, [payrollId]: data }));
    } catch { toast.error('Failed to load payroll items'); }
    finally { setLoadingItems(prev => ({ ...prev, [payrollId]: false })); }
  };

  const toggleExpand = (id) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    loadItems(id);
  };

  const handleAction = async (id, action) => {
    setActionLoading(prev => ({ ...prev, [id]: action }));
    try {
      if (action === 'submit') await companyAdminAPI.submitPayroll(id);
      else if (action === 'approve') await companyAdminAPI.approvePayroll(id);
      else if (action === 'cancel') await companyAdminAPI.cancelPayroll(id);
      toast.success(`Payroll ${action}ed successfully`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${action} payroll`);
    } finally { setActionLoading(prev => ({ ...prev, [id]: null })); }
  };

  const handleFund = async (id) => {
    if (!fundRef.trim() || !fundAmount || parseFloat(fundAmount) <= 0) {
      toast.error('Enter valid funding reference and amount');
      return;
    }
    setActionLoading(prev => ({ ...prev, [id]: 'fund' }));
    try {
      await companyAdminAPI.fundPayroll(id, fundRef.trim(), parseFloat(fundAmount));
      toast.success('Payroll funded successfully');
      setFundDialog(null);
      setFundRef('');
      setFundAmount('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to fund payroll');
    } finally { setActionLoading(prev => ({ ...prev, [id]: null })); }
  };

  const handleDisburse = async (id) => {
    setDisbursing(prev => ({ ...prev, [id]: true }));
    try {
      const result = await companyAdminAPI.disbursePayroll(id);
      setDisburseResults(result);
      toast.success(`Disbursement complete: ${result.items_processed} succeeded, ${result.items_failed} failed`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Disbursement failed');
    } finally { setDisbursing(prev => ({ ...prev, [id]: false })); }
  };

  if (loading) return (
    <div className="flex justify-center py-20">
      <Loader2 className="w-8 h-8 animate-spin text-pink-400" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="payroll-management">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Payroll</h1>
          <p className="text-sm text-gray-500">{payrolls.length} payroll run{payrolls.length !== 1 ? 's' : ''}</p>
        </div>
      </div>

      {payrolls.length === 0 ? (
        <Card className="border-gray-200">
          <CardContent className="p-12 text-center">
            <FileSpreadsheet className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-400 text-sm">No payroll runs yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {payrolls.map(p => {
            const cfg = STATUS_CONFIG[p.status] || STATUS_CONFIG.draft;
            const StatusIcon = cfg.icon;
            const isExpanded = expandedId === p.id;
            const isProcessing = disbursing[p.id];

            return (
              <Card key={p.id} className="border-gray-200 shadow-sm overflow-hidden" data-testid={`payroll-card-${p.id}`}>
                <CardContent className="p-0">
                  {/* Header Row */}
                  <div
                    className="p-4 cursor-pointer hover:bg-gray-50/50 transition-colors"
                    onClick={() => toggleExpand(p.id)}
                    data-testid={`payroll-row-${p.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${cfg.bg} ${cfg.text}`}>
                          <StatusIcon className={`w-4 h-4 ${p.status === 'processing' ? 'animate-spin' : ''}`} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{p.period_label || 'Payroll Run'}</p>
                          <p className="text-xs text-gray-500">
                            <Users className="w-3 h-3 inline mr-1" />
                            {p.employee_count} employee{p.employee_count !== 1 ? 's' : ''}
                            {p.notes && <span className="ml-2 text-gray-400">- {p.notes}</span>}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right hidden sm:block">
                          <p className="text-sm font-bold text-gray-900">
                            NGN {Number(p.total_net || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </p>
                          <p className="text-[10px] text-gray-400">Net Pay</p>
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${cfg.bg} ${cfg.text}`}>
                          {cfg.label}
                        </span>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="border-t border-gray-100 bg-gray-50/30">
                      {/* Summary Grid */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4">
                        <SummaryItem label="Gross" value={p.total_gross} />
                        <SummaryItem label="Deductions" value={p.total_deductions} />
                        <SummaryItem label="Benefits" value={p.total_benefits} />
                        <SummaryItem label="Net Pay" value={p.total_net} highlight />
                      </div>

                      {/* Funding Info */}
                      {p.funding_reference && (
                        <div className="px-4 pb-2">
                          <div className="bg-emerald-50 rounded-lg p-3 text-xs text-emerald-700">
                            <DollarSign className="w-3 h-3 inline mr-1" />
                            Funded: NGN {Number(p.funding_amount || 0).toLocaleString()} (Ref: {p.funding_reference})
                          </div>
                        </div>
                      )}

                      {/* Processing Results */}
                      {(p.items_processed > 0 || p.items_failed > 0) && (
                        <div className="px-4 pb-2">
                          <div className="bg-white rounded-lg border border-gray-200 p-3 text-xs">
                            <span className="text-green-600 font-medium">{p.items_processed} processed</span>
                            {p.items_failed > 0 && (
                              <span className="text-red-500 font-medium ml-3">{p.items_failed} failed</span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Employee Items */}
                      <div className="px-4 pb-3">
                        {loadingItems[p.id] ? (
                          <div className="text-center py-4">
                            <Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" />
                          </div>
                        ) : items[p.id] && items[p.id].length > 0 ? (
                          <div className="space-y-1">
                            <p className="text-xs font-medium text-gray-500 mb-2">Employees</p>
                            {items[p.id].map(item => {
                              const itemCfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.draft;
                              return (
                                <div key={item.id} className="flex items-center justify-between bg-white rounded-lg border border-gray-100 px-3 py-2 text-xs">
                                  <div className="flex items-center gap-2">
                                    <span className={`w-1.5 h-1.5 rounded-full ${item.status === 'completed' ? 'bg-green-500' : item.status === 'failed' ? 'bg-red-500' : 'bg-gray-400'}`} />
                                    <span className="text-gray-700 font-medium">{item.employee_id.slice(0, 8)}...</span>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <span className="text-gray-900 font-semibold">
                                      NGN {Number(item.net_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                    </span>
                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${itemCfg.bg} ${itemCfg.text}`}>
                                      {item.status}
                                    </span>
                                  </div>
                                  {item.failure_reason && (
                                    <span className="text-red-400 text-[10px] block w-full mt-1">{item.failure_reason}</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : null}
                      </div>

                      {/* Actions */}
                      <div className="px-4 pb-4 flex flex-wrap gap-2">
                        {p.status === 'draft' && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleAction(p.id, 'submit')}
                              disabled={!!actionLoading[p.id]}
                              className="text-xs bg-pink-500 hover:bg-pink-600 text-white h-8"
                              data-testid={`submit-payroll-${p.id}`}
                            >
                              {actionLoading[p.id] === 'submit' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
                              Submit for Approval
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleAction(p.id, 'cancel')}
                              disabled={!!actionLoading[p.id]}
                              className="text-xs h-8 text-red-500 border-red-200 hover:bg-red-50"
                              data-testid={`cancel-payroll-${p.id}`}
                            >
                              Cancel
                            </Button>
                          </>
                        )}

                        {p.status === 'pending_approval' && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleAction(p.id, 'approve')}
                              disabled={!!actionLoading[p.id]}
                              className="text-xs bg-blue-500 hover:bg-blue-600 text-white h-8"
                              data-testid={`approve-payroll-${p.id}`}
                            >
                              {actionLoading[p.id] === 'approve' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle className="w-3 h-3 mr-1" />}
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleAction(p.id, 'cancel')}
                              disabled={!!actionLoading[p.id]}
                              className="text-xs h-8 text-red-500 border-red-200 hover:bg-red-50"
                            >
                              Cancel
                            </Button>
                          </>
                        )}

                        {p.status === 'approved' && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => { setFundDialog(p); setFundAmount(String(p.total_net)); }}
                              className="text-xs bg-emerald-500 hover:bg-emerald-600 text-white h-8"
                              data-testid={`fund-payroll-${p.id}`}
                            >
                              <DollarSign className="w-3 h-3 mr-1" /> Fund Payroll
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleAction(p.id, 'cancel')}
                              disabled={!!actionLoading[p.id]}
                              className="text-xs h-8 text-red-500 border-red-200 hover:bg-red-50"
                            >
                              Cancel
                            </Button>
                          </>
                        )}

                        {p.status === 'funded' && (
                          <Button
                            size="sm"
                            onClick={() => handleDisburse(p.id)}
                            disabled={isProcessing}
                            className="text-xs bg-purple-600 hover:bg-purple-700 text-white h-8"
                            data-testid={`disburse-payroll-${p.id}`}
                          >
                            {isProcessing
                              ? <><Loader2 className="w-3 h-3 animate-spin mr-1" /> Disbursing...</>
                              : <><Banknote className="w-3 h-3 mr-1" /> Disburse to Banks</>
                            }
                          </Button>
                        )}

                        {(p.status === 'failed' || p.status === 'partially_completed') && (
                          <Button
                            size="sm"
                            onClick={() => handleDisburse(p.id)}
                            disabled={isProcessing}
                            className="text-xs bg-orange-500 hover:bg-orange-600 text-white h-8"
                            data-testid={`retry-disburse-${p.id}`}
                          >
                            {isProcessing
                              ? <><Loader2 className="w-3 h-3 animate-spin mr-1" /> Retrying...</>
                              : <><AlertTriangle className="w-3 h-3 mr-1" /> Retry Disbursement</>
                            }
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Fund Dialog */}
      <Dialog open={!!fundDialog} onOpenChange={(open) => { if (!open) setFundDialog(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="fund-payroll-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">Fund Payroll</DialogTitle>
          </DialogHeader>
          {fundDialog && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <p className="text-gray-600">Period: <span className="font-semibold text-gray-900">{fundDialog.period_label}</span></p>
                <p className="text-gray-600">Required: <span className="font-semibold text-gray-900">NGN {Number(fundDialog.total_net || 0).toLocaleString()}</span></p>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium text-gray-700">Funding Reference</Label>
                <Input
                  value={fundRef}
                  onChange={(e) => setFundRef(e.target.value)}
                  placeholder="e.g. Bank transfer ref"
                  data-testid="fund-ref-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium text-gray-700">Amount (NGN)</Label>
                <Input
                  type="number"
                  value={fundAmount}
                  onChange={(e) => setFundAmount(e.target.value)}
                  placeholder="Amount"
                  data-testid="fund-amount-input"
                />
              </div>
              <Button
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
                onClick={() => handleFund(fundDialog.id)}
                disabled={actionLoading[fundDialog.id] === 'fund'}
                data-testid="confirm-fund-btn"
              >
                {actionLoading[fundDialog.id] === 'fund'
                  ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  : <DollarSign className="w-4 h-4 mr-2" />
                }
                Confirm Funding
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Disbursement Results Dialog */}
      <Dialog open={!!disburseResults} onOpenChange={(open) => { if (!open) setDisburseResults(null); }}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto" data-testid="disburse-results-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">Disbursement Results</DialogTitle>
          </DialogHeader>
          {disburseResults && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-blue-700">{disburseResults.total_items}</p>
                  <p className="text-[10px] text-blue-500">Total</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-green-700">{disburseResults.items_processed}</p>
                  <p className="text-[10px] text-green-500">Success</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-red-700">{disburseResults.items_failed}</p>
                  <p className="text-[10px] text-red-500">Failed</p>
                </div>
              </div>
              <div className="space-y-2">
                {disburseResults.results?.map((r, i) => (
                  <div key={i} className={`rounded-lg border px-3 py-2 text-xs ${r.status === 'completed' ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="font-semibold text-gray-900">{r.employee_name || r.employee_id?.slice(0, 8)}</span>
                        {r.bank_name && <span className="text-gray-500 ml-2">{r.bank_name} {r.account_number}</span>}
                      </div>
                      <div className="text-right">
                        <span className="font-bold text-gray-900">NGN {Number(r.amount || 0).toLocaleString()}</span>
                        <span className={`ml-2 ${r.status === 'completed' ? 'text-green-600' : 'text-red-600'}`}>
                          {r.status === 'completed' ? 'Sent' : 'Failed'}
                        </span>
                      </div>
                    </div>
                    {r.error && <p className="text-red-500 mt-1">{r.error}</p>}
                    {r.reference && <p className="text-gray-400 mt-0.5">Ref: {r.reference}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryItem({ label, value, highlight }) {
  return (
    <div className="bg-white rounded-lg border border-gray-100 p-3 text-center">
      <p className={`text-sm font-bold ${highlight ? 'text-pink-600' : 'text-gray-900'}`}>
        NGN {Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
      </p>
      <p className="text-[10px] text-gray-400 mt-0.5">{label}</p>
    </div>
  );
}
